/**
 * UtilitiesTab Component
 *
 * Allows contractors to manage utility providers (electricity, natural gas, and propane)
 * with custom rates for accurate assessment calculations.
 *
 * Features:
 * - Add/edit/delete utility providers
 * - Configure provider name, type, rate, and notes
 * - Technician selects provider during each assessment
 * - Mobile-responsive card layout
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

export default function UtilitiesTab() {
  const { contractor } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingProvider, setEditingProvider] = useState(null);

  // Form state
  const [providerName, setProviderName] = useState('');
  const [utilityType, setUtilityType] = useState('electric');
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');

  // Load providers
  useEffect(() => {
    if (contractor?.id) {
      loadProviders();
    }
  }, [contractor?.id]);

  async function loadProviders() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('utility_providers')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('utility_type')
        .order('provider_name');

      if (error) throw error;

      setProviders(data || []);
    } catch (err) {
      console.error('Error loading providers:', err);
      setError('Failed to load utility providers');
    } finally {
      setLoading(false);
    }
  }

  // Handle utility type change
  function handleUtilityTypeChange(newType) {
    setUtilityType(newType);
    setProviderName('');
    setRate('');
  }

  // Open add modal
  function handleAdd() {
    console.log('handleAdd called - opening modal');
    setModalMode('add');
    setProviderName('');
    setUtilityType(''); // Empty string forces user to select
    setRate('');
    setNotes('');
    setEditingProvider(null);
    setShowModal(true);
    setError(null);
    console.log('Modal state set to true');
  }

  // Open edit modal
  function handleEdit(provider) {
    setModalMode('edit');
    setProviderName(provider.provider_name);
    setUtilityType(provider.utility_type);
    setRate(provider.rate.toString());
    setNotes(provider.notes || '');
    setEditingProvider(provider);
    setShowModal(true);
    setError(null);
  }

  // Close modal
  function handleCloseModal() {
    console.log('handleCloseModal called');
    setShowModal(false);
    setProviderName('');
    setUtilityType('electric');
    setRate('');
    setNotes('');
    setEditingProvider(null);
    setError(null);
  }

  // Save provider (add or edit)
  async function handleSave(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent Settings form
    console.log('handleSave called', { providerName, utilityType, rate });

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate inputs
      if (!providerName.trim()) {
        throw new Error('Provider name is required');
      }

      // Validate rate
      const rateNum = parseFloat(rate);
      if (isNaN(rateNum) || rateNum <= 0 || rateNum >= 10) {
        setError('Rate must be between $0 and $10');
        setSaving(false);
        return;
      }

      const unit = utilityType === 'electric' ? 'kwh' : utilityType === 'gas' ? 'therm' : 'gallon';

      const providerData = {
        contractor_id: contractor.id,
        provider_name: providerName.trim(),
        utility_type: utilityType,
        rate: rateNum,
        unit: unit,
        notes: notes.trim() || null
      };

      console.log('Saving provider data:', providerData);

      if (modalMode === 'edit') {
        // Update existing provider
        const { error: updateError } = await supabase
          .from('utility_providers')
          .update(providerData)
          .eq('id', editingProvider.id);

        if (updateError) throw updateError;
        console.log('Provider updated successfully');
      } else {
        // Insert new provider
        const { data: insertData, error: insertError } = await supabase
          .from('utility_providers')
          .insert(providerData)
          .select()
          .single();

        if (insertError) throw insertError;
        console.log('Provider inserted successfully');
      }

      // Reload providers
      await loadProviders();
      console.log('Providers reloaded');

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      handleCloseModal();
    } catch (err) {
      console.error('Error saving provider:', err);
      setError(err.message || 'Failed to save provider');
      setSaving(false);
    } finally {
      setSaving(false);
    }
  }

  // Delete provider
  async function handleDelete(providerId) {
    if (!confirm('Are you sure you want to delete this provider? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('utility_providers')
        .delete()
        .eq('id', providerId);

      if (deleteError) throw deleteError;

      await loadProviders();

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting provider:', err);
      setError(err.message || 'Failed to delete provider');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '16px', color: '#64748b' }}>Loading utility providers...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div style={{
          padding: '15px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <strong>Provider saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '15px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        padding: '15px',
        background: '#f0f9ff',
        borderRadius: '8px',
        marginBottom: '24px',
        fontSize: '14px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        <strong>💡 About Utility Providers:</strong> Configure the electricity, natural gas, and propane providers available in your service area. When creating an assessment, ask your customer which provider they use and select it from your configured list.
      </div>

      {/* Utility Providers Section */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', margin: 0 }}>⚡ Utility Providers</h2>
          <button
            onClick={handleAdd}
            className="btn btn-primary"
            disabled={saving}
          >
            + Add Provider
          </button>
        </div>

        {providers.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '8px',
            color: '#64748b'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '15px' }}>⚡🔵🟠</div>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No utility providers configured</div>
            <div style={{ fontSize: '14px', marginBottom: '20px' }}>Add electricity, natural gas, and propane providers to get started</div>
            <button
              onClick={handleAdd}
              className="btn btn-primary"
              disabled={saving}
            >
              + Add Your First Provider
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {providers.map(provider => {
              return (
                <div key={provider.id} style={{
                  padding: '16px',
                  background: '#ffffff',
                  border: '2px solid #e0e6ed',
                  borderRadius: '8px',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '16px',
                  alignItems: 'start'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '20px' }}>
                        {provider.utility_type === 'electric' ? '⚡' : provider.utility_type === 'gas' ? '🔵' : '🟠'}
                      </span>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                        {provider.provider_name}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: provider.utility_type === 'electric' ? '#667eea' : provider.utility_type === 'gas' ? '#3b82f6' : '#f97316',
                      marginBottom: '4px',
                      marginLeft: '30px'
                    }}>
                      ${provider.rate.toFixed(4)} / {provider.unit}
                    </div>
                    {provider.notes && (
                      <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', marginLeft: '30px' }}>
                        {provider.notes}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => handleEdit(provider)}
                      className="btn btn-secondary"
                      style={{ fontSize: '13px', padding: '6px 12px' }}
                      disabled={saving}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(provider.id)}
                      className="btn"
                      style={{
                        fontSize: '13px',
                        padding: '6px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '2px solid #ef4444'
                      }}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - Using Portal to render outside form hierarchy */}
      {showModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '20px', marginBottom: '20px' }}>
              {modalMode === 'edit' ? 'Edit Provider' : 'Add New Provider'}
            </h3>

            <form onSubmit={handleSave}>
              {/* STEP 1: Utility Type First */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Utility Type <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {modalMode === 'edit' ? (
                  <>
                    <div style={{
                      padding: '12px',
                      background: '#f8fafc',
                      border: '2px solid #e0e6ed',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#2c3e50'
                    }}>
                      {utilityType === 'electric' ? '⚡ Electricity' : utilityType === 'gas' ? '🔵 Natural Gas' : '🟠 Propane'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                      Utility type cannot be changed after creation
                    </div>
                  </>
                ) : (
                  <select
                    className="input"
                    value={utilityType}
                    onChange={(e) => handleUtilityTypeChange(e.target.value)}
                    required
                  >
                    <option value="">-- Select Utility Type --</option>
                    <option value="electric">⚡ Electricity</option>
                    <option value="gas">🔵 Natural Gas</option>
                    <option value="propane">🟠 Propane</option>
                  </select>
                )}
              </div>

              {/* STEP 2: Provider Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Provider Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={providerName}
                  onChange={(e) => setProviderName(e.target.value)}
                  placeholder="Duke Energy"
                  required
                  autoFocus={modalMode === 'edit'}
                />
              </div>

              {/* STEP 4: Rate */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Rate (per {utilityType === 'electric' ? 'kWh' : utilityType === 'gas' ? 'therm' : 'gallon'}) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#64748b',
                    fontWeight: '600'
                  }}>$</span>
                  <input
                    type="number"
                    className="input"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="0.1200"
                    step="0.0001"
                    min="0"
                    max="10"
                    style={{ paddingLeft: '28px' }}
                    required
                  />
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                  Enter rate between $0 and $10
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Notes (Optional)
                </label>
                <textarea
                  className="input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Service area, rate tiers, special notes..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (modalMode === 'edit' ? 'Update Provider' : 'Add Provider')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
